import { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';
import { authService } from '../../services/authService';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { intusLogoutUrl, redirectToIntusLogout } from '../../utils/intusRedirect';
import ConsultorioModal from '../ConsultorioModal';
import CampanaNotificaciones from './CampanaNotificaciones';
import {
  Home,
  Brain,
  Heart,
  Users,
  Video,
  Gamepad2,
  User,
  LogOut,
  Menu,
  X,
  Shield,
  Target,
  Building2
} from 'lucide-react';

interface MainLayoutProps {
  children: ReactNode;
}

// Áreas con feature flag (las que puede habilitar/deshabilitar el admin).
const flaggedNavigation: { href: string; flag: string }[] = [
  { href: '/evaluacion', flag: 'evaluaciones' },
  { href: '/ejercicios', flag: 'ejercicios_juegos' },
  { href: '/videos', flag: 'videos' },
  { href: '/comunidad', flag: 'comunidad' },
  { href: '/juegos', flag: 'ejercicios_juegos' },
  { href: '/informe-padres', flag: 'informes' },
];

/** Slot "Más" de la barra inferior: abre el sheet con el resto de destinos. */
const MORE_ITEM = { name: 'Más', href: '/__mas__', icon: Menu } as const;

/**
 * Tono de cada destino de la barra inferior: **uno por modulo, siempre**.
 *
 * Cada destino lleva su color aunque no esté seleccionado. Es lo que hace la
 * barra reconocible de un vistazo y le da identidad a cada modulo. Lo único que
 * cambia al navegar es la INTENSIDAD, nunca el tono.
 *
 * POR QUÉ EL COLOR VA EN UN CUADRO Y NO SUELTO
 * ---------------------------------------------
 * Medido sobre el fondo real de la barra (`--v-surface` al 95%), los `-500`
 * sueltos fallan en claro: `mint` daba 2.33:1 y `lima` 1.92:1, por debajo del
 * 3:1 que WCAG 1.4.11 exige a un elemento gráfico. "Los cinco con color vivo" y
 * "los cinco con el color correcto" no caben en el mismo diseño con el icono
 * desnudo: habría que bajar el rojo y el violeta, y el rojo es justo el que se
 * pidió.
 *
 * En un cuadro, la tinta 700 sobre su container 100 da 5.30:1 a 9.75:1 en los
 * dos temas, y el tono sigue siendo reconocible. Además, el estado activo ya no
 * puede apoyarse en el color (cada destino tiene el suyo), así que se resuelve
 * con la misma escalera que usa el resto de la app: inactivo = container 100 con
 * tinta 700, activo = acento 500 con `--v-ink-acento`.
 *
 * `mint` no tiene trío 100/500/700: usa los tokens del eje vital, que es lo
 * mismo que hace `ACENTOS`.
 *
 * REGLA: las clases van ESCRITAS, no construidas con `bg-v-${slot}-100`. Tailwind
 * purga por `content`, así que una clase que solo existe en runtime no se emite:
 * el build pasa en verde y el cuadro sale sin fondo. Es el mismo bug que
 * `bg-v-verde-500` en el dashboard, y por eso `check:tokens` vigila que las
 * utilities de los siete slots lleguen al CSS emitido.
 */
const TONOS_BARRA_INFERIOR = {
  '/dashboard': {
    inactivo: 'bg-v-mint-container text-v-mint-text',
    activo: 'bg-v-mint text-v-mint-ink',
  },
  '/ejercicios': {
    inactivo: 'bg-v-rojo-100 text-v-rojo-700',
    activo: 'bg-v-rojo-500 text-v-ink-acento',
  },
  '/comunidad': {
    inactivo: 'bg-v-azul-100 text-v-azul-700',
    activo: 'bg-v-azul-500 text-v-ink-acento',
  },
  // "Más" no es un modulo, pero se le da tono igual: un icono neutro en medio de
  // cuatro de color lee como elemento deshabilitado, y no lo esta.
  '/__mas__': {
    inactivo: 'bg-v-lima-100 text-v-lima-700',
    activo: 'bg-v-lima-500 text-v-ink-acento',
  },
} as const;

/** Si alguien anade un destino sin tono, que salga neutro y no invisible. */
const TONO_SIN_DEFINIR = {
  inactivo: 'bg-v-background text-v-on-surface-variant',
  activo: 'bg-v-on-surface-variant text-v-background',
} as const;

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'Evaluación', href: '/evaluacion', icon: Brain },
  { name: 'Ejercicios', href: '/ejercicios', icon: Heart },
  { name: 'Videos', href: '/videos', icon: Video },
  { name: 'Comunidad y Foros', href: '/comunidad', icon: Users },
  { name: 'Juegos', href: '/juegos', icon: Gamepad2 },
  { name: 'Mis Metas', href: '/metas', icon: Target },
  { name: 'Perfil', href: '/perfil', icon: User },
];

const psychologistNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'Alertas', href: '/alertas-psicologo', icon: Shield },
  // El profesional también puede realizar las actividades como un paciente
  // (ejercicios, juegos, evaluación, etc.) con su propia sesión.
  { name: 'Evaluación', href: '/evaluacion', icon: Brain },
  { name: 'Ejercicios', href: '/ejercicios', icon: Heart },
  { name: 'Videos', href: '/videos', icon: Video },
  { name: 'Comunidad y Foros', href: '/comunidad', icon: Users },
  { name: 'Juegos', href: '/juegos', icon: Gamepad2 },
  { name: 'Mis Metas', href: '/metas', icon: Target },
  { name: 'Perfil', href: '/perfil', icon: User },
];

export default function MainLayout({ children }: MainLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { theme, toggleTheme, isMobileMenuOpen, setMobileMenuOpen, openModal } = useUIStore();
  const { isEnabled } = useFeatureFlags();

  const handleLogout = async () => {
    const { refreshToken } = useAuthStore.getState();
    if (refreshToken) {
      try {
        await authService.logout(refreshToken);
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    logout();
    // Cierre completo: además de la sesión de Contigo, se cierra la sesión
    // del paciente en Vita360 (logout-remote) para que no quede rastro. Aterriza
    // en la bienvenida del portal paciente de INTUS.
    if (intusLogoutUrl()) {
      redirectToIntusLogout();
    } else {
      navigate('/login');
    }
  };

  /**
   * Destino activo, incluidas sus subpantallas.
   *
   * `/ejercicios/1` (la pantalla de un ejercicio) pertenece a la seccion
   * Ejercicios: sin este prefijo, el titulo del header caia a "Vita360 App" y el
   * item de la barra inferior perdia `aria-current="page"` justo en la pantalla
   * en la que el usuario esta haciendo el ejercicio.
   */
  const isActiveRoute = (href: string) =>
    location.pathname === href || location.pathname.startsWith(`${href}/`);

  const renderNavigation = () => {
    const navItems = user?.rol === 'PSICOLOGO' ? psychologistNavigation : navigation;

    // Oculta del menú las áreas deshabilitadas por los feature flags.
    const visibleItems = navItems.filter((item) => {
      const flagged = flaggedNavigation.find((f) => f.href === item.href);
      return flagged ? isEnabled(flagged.flag) : true;
    });

    return (
      <nav className="space-y-1">
        {visibleItems.map((item) => (
          <button
            key={item.name}
            onClick={() => {
              navigate(item.href);
              setMobileMenuOpen(false);
            }}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
              isActiveRoute(item.href)
                ? 'bg-primary text-primary-foreground'
                : 'text-v-on-surface-variant hover:bg-accent hover:text-accent-foreground'
            }`}
          >
            <item.icon className="h-5 w-5" />
            <span>{item.name}</span>
          </button>
        ))}
      </nav>
    );
  };

  /** Ítems de la barra inferior: los destinos más usados (Dashboard no tiene
   *  feature flag) + "Más", que abre el sheet con el resto. Se filtran con los
   *  mismos feature flags que el menú lateral para no ofrecer áreas apagadas. */
  const bottomNavBase = [
    navigation.find((n) => n.href === '/dashboard'),
    navigation.find((n) => n.href === '/ejercicios'),
    navigation.find((n) => n.href === '/comunidad'),
  ];
  const visibleBottomNavItems = [
    ...bottomNavBase.filter((item): item is (typeof navigation)[number] => {
      if (!item) return false;
      const flagged = flaggedNavigation.find((f) => f.href === item.href);
      return flagged ? isEnabled(flagged.flag) : true;
    }),
    MORE_ITEM,
  ];

  /** Título de la página actual; en móvil sustituye a la marca en el header. */
  const pageTitle =
    (user?.rol === 'PSICOLOGO' ? psychologistNavigation : navigation).find((item) =>
      isActiveRoute(item.href),
    )?.name ?? 'Vita360 App';

  return (
    <div className="min-h-dvh bg-v-background">
      {/* h-dvh (no h-screen): 100vh ignora la barra de direcciones móvil y
          corta el footer. `scroll-nativo` evita el pull-to-refresh del
          navegador sobre un contenedor con scroll propio. */}
      <div className="flex h-dvh bg-v-background scroll-nativo">
        {/* Desktop Sidebar */}
        <aside className="hidden md:flex flex-col w-64 border-r bg-card">
          <div className="p-6">
            {/* La marca no es un encabezado: el h1 es el titulo de la seccion,
                igual que en la vista movil. Antes el sidebar usaba h1 y el
                titulo h2, al reves. */}
            <p className="text-2xl font-bold text-primary">Vita360 App</p>
            <p className="text-sm text-v-on-surface-variant mt-1">
              {user?.nombre}
            </p>
          </div>

          {/* Botón Consultorio: acceso al dashboard del paciente en Vita360,
              sutilmente llamativo (gradiente suave + icono) */}
          <div className="px-4 pb-2">
            <button
              type="button"
              onClick={() => openModal('consultorio')}
              className="group flex w-full items-center gap-3 rounded-xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent px-4 py-3 text-left ring-1 ring-primary/20 transition-all hover:ring-primary/40 hover:from-primary/15"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm transition-transform group-hover:scale-105">
                <Building2 className="h-5 w-5" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-primary">Consultorio</span>
                <span className="block truncate text-xs text-v-on-surface-variant">Mi espacio en Vita360</span>
              </span>
            </button>
          </div>

          <div className="flex-1 px-4 overflow-y-auto">
            {renderNavigation()}
          </div>

          <div className="p-4 border-t">
            <button
              onClick={toggleTheme}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg bg-accent hover:bg-accent/80 transition-colors"
            >
              {theme === 'light' ? '🌙' : '☀️'}
              <span>{theme === 'light' ? 'Modo Oscuro' : 'Modo Claro'}</span>
            </button>
          </div>

          <div className="p-4 border-t">
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-2 px-4 py-2 rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
            >
              <LogOut className="h-5 w-5" />
              <span>Cerrar Sesión</span>
            </button>
          </div>
        </aside>

        {/* Columna unica para movil y escritorio. Antes eran dos ramas
            (`md:hidden` y `hidden md:flex`) y CADA UNA pintaba `{children}`:
            las dos copias vivian en el DOM a la vez, separadas solo por CSS.
            Eso duplicaba los `id` (titulo-acciones, titulo-semana), las regiones
            `aria-live`, los `h1`, y ejecutaba los `useEffect` de cada pagina DOS
            veces (doble peticion en todas las pantallas). Ahora se renderiza una
            sola vez y lo que cambia por breakpoint es el cromo que la rodea. */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Mobile Header — sticky, con safe-area y targets de 48px */}
          <header
            className="no-tap-highlight sticky top-0 z-20 flex items-center justify-between
                       border-b border-v-outline bg-v-surface-90 px-4 py-3 backdrop-blur safe-t
                       md:hidden"
          >
            <div className="min-w-0">
              <h1 className="truncate text-lg font-extrabold tracking-tight text-v-on-surface">
                {pageTitle}
              </h1>
              <p className="truncate text-xs text-v-on-surface-variant">{user?.nombre}</p>
            </div>

            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={() => openModal('consultorio')}
                aria-label="Consultorio: mi espacio en Vita360"
                className="grid h-12 w-12 place-items-center rounded-full text-v-primary
                           transition-colors active:bg-black/5"
              >
                <Building2 className="h-5 w-5" />
              </button>
              <CampanaNotificaciones variante="movil" />
              <button
                type="button"
                onClick={toggleTheme}
                aria-label={theme === 'light' ? 'Activar modo oscuro' : 'Activar modo claro'}
                className="grid h-12 w-12 place-items-center rounded-full text-lg
                           text-v-on-surface transition-colors active:bg-black/5"
              >
                {theme === 'light' ? '🌙' : '☀️'}
              </button>
            </div>
          </header>

          {/* Mobile Menu — hoja inferior (sheet), no pantalla completa */}
  {/* Cabecera de escritorio: hermana de la movil, no una segunda columna con su
    propio `<main>`. */}
  <header className="hidden md:flex items-center justify-between p-6 border-b bg-card">
    <div>
      {/* h1 también en escritorio: la marca del sidebar es un <p>, así
          la página tiene un único h1 en cualquiera de las dos vistas. */}
      <h1 className="text-2xl font-bold">{pageTitle}</h1>
    </div>
    
    <div className="flex items-center space-x-4">
      {/* La campana (con su biñeta) es un componente propio: el nombre
          accesible y el punto rojo —solo con notificaciones sin leer— viven
          dentro. `h-12 w-12` (48px) igual que en movil: el header de
          escritorio venia con `p-2` (~36px). */}
      <CampanaNotificaciones variante="escritorio" />

      <button
        onClick={toggleTheme}
        aria-label={theme === 'light' ? 'Activar modo oscuro' : 'Activar modo claro'}
        className="grid h-12 w-12 place-items-center rounded-lg hover:bg-accent text-lg"
      >
        {theme === 'light' ? '🌙' : '☀️'}
      </button>

      <div className="flex items-center space-x-3">
        <div
          aria-hidden
          className="h-10 w-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold"
        >
          {user?.nombre?.charAt(0) || 'U'}
        </div>
      </div>
    </div>
  </header>
          {isMobileMenuOpen && (
            <div
              className="fixed inset-0 z-50 flex flex-col justify-end bg-black/50 backdrop-blur-sm"
              role="dialog"
              aria-modal="true"
              aria-label="Menú de navegación"
              onClick={(e) => {
                if (e.target === e.currentTarget) setMobileMenuOpen(false);
              }}
            >
              <div className="flex max-h-[85dvh] flex-col rounded-t-3xl bg-v-surface safe-b">
                <div className="flex justify-center pt-3">
                  <span aria-hidden className="h-1.5 w-10 rounded-full bg-v-outline" />
                </div>

                <div className="flex items-center justify-between px-5 pb-3 pt-3">
                  <h2 className="text-lg font-extrabold text-v-on-surface">Menú</h2>
                  <button
                    type="button"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-label="Cerrar menú"
                    className="grid h-12 w-12 place-items-center rounded-full text-v-on-surface
                               transition-colors active:bg-black/5"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="px-4 pb-2">
                  <button
                    type="button"
                    onClick={() => {
                      openModal('consultorio');
                      setMobileMenuOpen(false);
                    }}
                    className="flex w-full items-center gap-3 rounded-card bg-v-primary-container
                               px-4 py-3 text-left transition-transform active:scale-[0.98]"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl
                                     bg-v-primary text-white shadow-sm">
                      <Building2 className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-v-on-primary-container">
                        Consultorio
                      </span>
                      <span className="block truncate text-xs text-v-on-primary-container">
                        Mi espacio en Vita360
                      </span>
                    </span>
                  </button>
                </div>

                <div className="scroll-nativo flex-1 px-4 py-2">{renderNavigation()}</div>

                <div className="space-y-2 border-t border-v-outline p-4">
                  <button
                    type="button"
                    onClick={toggleTheme}
                    className="flex min-h-12 w-full items-center justify-center gap-2
                               rounded-pill bg-v-background px-4 text-v-on-surface
                               transition-colors active:bg-black/5"
                  >
                    <span aria-hidden>{theme === 'light' ? '🌙' : '☀️'}</span>
                    <span>{theme === 'light' ? 'Modo Oscuro' : 'Modo Claro'}</span>
                  </button>

                  {/* `bg-destructive/10` y NO `bg-v-error/10`: la rampa
                      `destructive` está definida como `hsl(var(--destructive))`
                      y Tailwind le puede inyectar el alfa; los tokens `v-*`
                      son hex puro y la clase con `/10` no se emite, así que el
                      botón salía sin fondo con el build en verde. Igual que el
                      botón de cerrar sesión del sidebar, que ya usaba la
                      rampa `destructive`. */}
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex min-h-12 w-full items-center justify-center gap-2
                               rounded-pill bg-destructive/10 px-4 text-destructive
                               transition-colors active:bg-destructive/20"
                  >
                    <LogOut className="h-5 w-5" />
                    <span>Cerrar Sesión</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Contenido: UNA sola copia, la que usan los dos tamaños. `pb-28` deja sitio a
            la barra inferior en movil; `md:p-6` es el padding del escritorio. */}
          <main className="scroll-nativo flex-1 px-4 pb-28 pt-4 md:p-6">{children}</main>

          {/* Barra de navegación inferior (solo móvil). Reemplaza a la
              hamburguesa comovia principal: 5 destinos al alcance del pulgar. */}
          <nav
            aria-label="Navegación principal"
            className="no-tap-highlight fixed inset-x-0 bottom-0 z-20 border-t
                       border-v-outline bg-v-surface-95 backdrop-blur safe-b md:hidden"
          >
            {/* `flex` y NO `grid grid-cols-5`: el numero de destinos cambia con
                los feature flags (si una de las tres areas esta apagada quedan 3,
                y con un grid fijo los destinos se agrupan a la izquierda dejando
                columnas vacias a la derecha). Con `flex-1` cada destino ocupa su
                parte del ancho sea cual sea el numero. */}
            <ul className="flex">
              {visibleBottomNavItems.map((item) => {
                const esMas = item.href === MORE_ITEM.href;
                const activo = esMas ? isMobileMenuOpen : isActiveRoute(item.href);
                const tono =
                  TONOS_BARRA_INFERIOR[item.href as keyof typeof TONOS_BARRA_INFERIOR] ??
                  TONO_SIN_DEFINIR;
                return (
                  <li key={item.name} className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => {
                        if (esMas) {
                          setMobileMenuOpen(true);
                        } else {
                          navigate(item.href);
                          setMobileMenuOpen(false);
                        }
                      }}
                      aria-current={activo ? 'page' : undefined}
                      className="flex h-16 w-full flex-col items-center justify-center gap-1
                                 px-0.5 transition-colors active:bg-black/5"
                    >
                      {/* El cuadro lleva el color y el icono. El `aria-hidden`
                          sube al envoltorio porque el nombre accesible del
                          destino es su etiqueta de texto: el icono repite lo que
                          el texto ya dice y solo añade ruido al lector de
                          pantalla. */}
                      <span
                        aria-hidden
                        className={`grid h-8 w-8 place-items-center rounded-xl transition-colors ${
                          tono.inactivo
                        } ${activo ? tono.activo : ''}`}
                      >
                        <item.icon className="h-[18px] w-[18px]" />
                      </span>
                      <span
                        className={`w-full truncate text-center text-[11px] leading-none ${
                          activo
                            ? 'font-bold text-v-on-surface'
                            : 'font-medium text-v-on-surface-variant'
                        }`}
                      >
                        {item.name}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </div>

      {/* Modal Consultorio (embebe el dashboard del paciente de Vita360) */}
      <ConsultorioModal />
    </div>
  );
}
